import { adminFetch, buildListParams, normalizeList } from "./_client";
import type { AdminReview, FlaggedMessage, FlaggedConversationListResponse, FlaggedConversationDetailResponse, MessagePatternResponse, AdminListParams, AdminListResponse, ModerationLock, ActiveModerationLock, ChatOffenderRow, ChatOffenderDetailResponse, HeldPhotoListResponse } from "./types";


export async function fetchAdminReviews(token: string, params: AdminListParams = {}): Promise<AdminListResponse<AdminReview>> {
  const sp = buildListParams({ filter: "unmoderated", ...params });
  const raw = await adminFetch<Record<string, unknown>>(`/admin/reviews?${sp}`, token);
  return normalizeList<AdminReview>(raw, "reviews");
}


export function moderateReview(token: string, id: number, action: string, editedComment?: string, reason?: string) {
  return adminFetch<{ success: boolean }>(`/admin/reviews/${id}/moderate`, token, {
    method: "POST",
    body: JSON.stringify({ action, editedComment, reason }),
  });
}


export function fetchActiveModerationLocks(token: string, type?: string) {
  const qs = type ? `?type=${encodeURIComponent(type)}` : "";
  return adminFetch<{ locks: ActiveModerationLock[] }>(`/admin/moderation-locks${qs}`, token);
}


export function fetchModerationLock(token: string, type: string, id: string | number) {
  return adminFetch<{ lock: ModerationLock | null }>(`/admin/moderation-locks/${type}/${id}`, token);
}


export function acquireModerationLock(token: string, type: string, id: string | number, force = false) {
  return adminFetch<{ lock: ModerationLock }>(`/admin/moderation-locks/${type}/${id}`, token, {
    method: "POST",
    body: JSON.stringify({ force }),
  });
}


export function releaseModerationLock(token: string, type: string, id: string | number) {
  return adminFetch<{ success: boolean }>(`/admin/moderation-locks/${type}/${id}`, token, { method: "DELETE" });
}


export function recordImageReveal(token: string, entity_type: string, entity_id: string | number, image_id?: string | number | null) {
  return adminFetch<{ success: boolean; revealed_at: string }>(`/admin/moderation-reveal`, token, {
    method: "POST",
    body: JSON.stringify({ entity_type, entity_id, image_id: image_id != null ? String(image_id) : null }),
  });
}


export function fetchFlaggedMessages(token: string, page = 1) {
  return adminFetch<{ messages: FlaggedMessage[]; total: number; page: number; limit: number }>(`/admin/messages/flagged?page=${page}`, token);
}


export function fetchRecentMessages(token: string, page = 1, search?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (search) params.set("search", search);
  return adminFetch<{ messages: FlaggedMessage[]; total: number; page: number; limit: number }>(`/admin/messages/recent?${params.toString()}`, token);
}


export function actionFlaggedMessage(
  token: string,
  id: number,
  action: string,
  opts: { note?: string; template_slug?: string } = {},
) {
  return adminFetch<{ success: boolean; action_id: number | null; applied: Record<string, unknown>; recovery?: string | null }>(
    `/admin/messages/${id}/action`,
    token,
    {
      method: "POST",
      body: JSON.stringify({ action, note: opts.note, template_slug: opts.template_slug }),
    },
  );
}


export function fetchFlaggedConversations(
  token: string,
  opts: { page?: number; limit?: number; view?: string; category?: string; search?: string } = {},
) {
  const qs = new URLSearchParams();
  if (opts.page) qs.set("page", String(opts.page));
  if (opts.limit) qs.set("limit", String(opts.limit));
  if (opts.view) qs.set("view", opts.view);
  if (opts.category) qs.set("category", opts.category);
  if (opts.search) qs.set("search", opts.search);
  const suffix = qs.toString() ? `?${qs}` : "";
  return adminFetch<FlaggedConversationListResponse>(`/admin/messages/conversations${suffix}`, token);
}


export function fetchFlaggedConversationDetail(token: string, conversationId: number) {
  return adminFetch<FlaggedConversationDetailResponse>(`/admin/messages/conversations/${conversationId}`, token);
}


// Task #7406 — re-run the classifier against every pending flagged message in
// a conversation and atomically clear any that are now clean under the updated
// rules. Returns a summary { rescanned, cleared, stillFlagged }.
export function rescanConversation(
  token: string,
  conversationId: number,
): Promise<{ rescanned: number; cleared: number; stillFlagged: number }> {
  return adminFetch<{ rescanned: number; cleared: number; stillFlagged: number }>(
    `/admin/messages/rescan-conversation`,
    token,
    {
      method: "POST",
      body: JSON.stringify({ conversationId }),
    },
  );
}


export function fetchMessagePatterns(token: string, opts: { phrase: string; excludeConversationId?: number }) {
  const qs = new URLSearchParams({ phrase: opts.phrase });
  if (opts.excludeConversationId) qs.set("exclude_conversation_id", String(opts.excludeConversationId));
  return adminFetch<MessagePatternResponse>(`/admin/messages/patterns?${qs}`, token);
}


// The server rejects > 200 ids per bulk-action call (it bounds the single SQL
// transaction). The whole-conversation "Dismiss"/"Approve" and the multi-select
// bulk toolbar can both exceed that — a spammy conversation may carry hundreds
// of flagged messages. Chunk client-side at that ceiling and sum the per-batch
// `count` so a large dismiss can never fail with "Maximum 200 ids" and strand
// the conversation in the queue. Each batch is its own atomic RPC; `mark_safe`
// and `acknowledge` are both idempotent over already-resolved rows (the RPC
// only acts on still-`is_filtered` ids), so a re-run never double-resolves.
const BULK_ACTION_MAX_IDS = 200;

export async function bulkActionMessages(
  token: string,
  // Task #5286 — bulk supports `mark_safe` (clear + reveal) and `acknowledge`
  // (audit-only "dismiss, keep hidden"). The all_pending drain stays mark_safe.
  payload: { ids: number[]; action: "mark_safe" | "acknowledge"; note?: string },
): Promise<{ success: boolean; count: number; applied_ids: number[] }> {
  const { ids, action, note } = payload;
  let total = 0;
  // Task #7514 — surface WHICH ids the server actually actioned. The RPC skips
  // ids that another moderator already resolved or that no longer carry a live
  // flag (`applied_count`/`applied_ids` reflect only the rows it touched), so
  // callers must reconcile from `applied_ids` rather than assuming every
  // requested id was resolved — otherwise a partially-applied bulk (a race
  // against another moderator) would wrongly dequeue a still-flagged thread.
  const appliedIds: number[] = [];
  for (let i = 0; i < ids.length; i += BULK_ACTION_MAX_IDS) {
    const chunk = ids.slice(i, i + BULK_ACTION_MAX_IDS);
    const r = await adminFetch<{ success: boolean; count: number; applied_ids?: number[] }>(`/admin/messages/bulk-action`, token, {
      method: "POST",
      body: JSON.stringify({ ids: chunk, action, note }),
    });
    total += r.count ?? 0;
    if (Array.isArray(r.applied_ids)) appliedIds.push(...r.applied_ids);
  }
  return { success: true, count: total, applied_ids: appliedIds };
}


// Task #6615 — held chat photo review queue: image attachments the AI held
// pre-booking, awaiting a human approve/reject decision.
export function fetchHeldPhotos(token: string, opts: { page?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (opts.page) qs.set("page", String(opts.page));
  if (opts.limit) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return adminFetch<HeldPhotoListResponse>(`/admin/messages/held-photos${suffix}`, token);
}


// Task #6615 — approve reveals the photo to both participants AND fires the
// recipient notification fan-out the held path skipped; reject keeps it
// hidden forever (audit-only acknowledge).
export function decideHeldPhoto(token: string, id: number, decision: "approve" | "reject", note?: string) {
  return adminFetch<{ success: boolean; action_id: number | null; decision: string; fanout_ok: boolean }>(
    `/admin/messages/${id}/photo-decision`,
    token,
    { method: "POST", body: JSON.stringify({ decision, note }) },
  );
}


// Task #4370 — count of currently-pending flagged messages (is_filtered=true
// with no terminal action), used to populate the "resolve all pending"
// confirmation step before anything is mutated.
export function fetchPendingMessageCount(token: string) {
  return adminFetch<{ count: number }>(`/admin/messages/pending-count`, token);
}


// Task #4370 — resolve the ENTIRE pending backlog as safe in one click. The
// server drains it in bounded batches and reports how many it applied (plus
// any still-remaining if a concurrent flood out-paced a single invocation).
export function resolveAllPendingMessages(token: string, note?: string) {
  return adminFetch<{ success: boolean; count: number; remaining: number; scope: "all_pending" }>(
    `/admin/messages/bulk-action`,
    token,
    {
      method: "POST",
      body: JSON.stringify({ scope: "all_pending", action: "mark_safe", note }),
    },
  );
}


// Post an admin or impersonated message directly into a conversation thread
// (no linked trip). Mirrors sendTripAdminReply for conversations that don't
// have an associated trip — e.g. pre-booking enquiry threads. When a trip_id
// is available, use sendTripAdminReply (in trips.ts) instead so the trip's
// renter↔owner conversation thread is correctly scoped to the car.
export function sendConversationAdminReply(
  token: string,
  conversationId: number,
  body: { content: string; reason: string; asUserId?: string },
) {
  return adminFetch<{ ok: boolean; messageId: number; sentAs: string; createdAt: string }>(
    `/admin/messages/conversations/${conversationId}/reply`,
    token,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}


// Task #5405 — Repeat Offender Enforcement Console. Ranked list of users with
// flagged chat activity by severity-weighted, recency-decayed score.
export async function fetchChatOffenders(
  token: string,
  opts: { window?: "7" | "30" | "all"; role?: "renter" | "host" | "all"; needsAction?: boolean; page?: number; pageSize?: number } = {},
): Promise<AdminListResponse<ChatOffenderRow>> {
  const qs = new URLSearchParams();
  if (opts.window) qs.set("window", opts.window);
  if (opts.role) qs.set("role", opts.role);
  if (opts.needsAction) qs.set("needs_action", "true");
  if (opts.page) qs.set("page", String(opts.page));
  if (opts.pageSize) qs.set("pageSize", String(opts.pageSize));
  const suffix = qs.toString() ? `?${qs}` : "";
  const raw = await adminFetch<Record<string, unknown>>(`/admin/messages/offenders${suffix}`, token);
  return normalizeList<ChatOffenderRow>(raw, "rows");
}


// Task #5405 — drill-in: a single offender's flagged messages + full
// warn/suspend/unsuspend/ban enforcement history.
export function fetchChatOffenderDetail(token: string, userId: number) {
  return adminFetch<ChatOffenderDetailResponse>(`/admin/messages/offenders/${userId}`, token);
}
